import React, { FC, ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddGroupModal from './modals/AddGroupModal';
import DeleteGroupModal from './modals/DeleteGroupModal';
import EditGroupModal from './modals/EditGroupModal';

import { Box, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DeviceGroupDto, MutateDeviceGroup } from '@thingslog/repositories';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@thingslog/ui-components';
import { useModal } from '@thingslog/ui-components';

interface DeviceGroupTableRow {
  id: number;
  deviceGroupId: number;
  deviceGroupName: string;
  parentDeviceGroupName: string;
  deviceSensorsCount: number;
}

//Component
const DeviceGroupTable: FC<DeviceGroupTableProps> = ({
  companyId,
  deviceGroups,
  autoDeviceGroupTypes,
  onSelectedDeviceGroupChange,
  selectedDeviceGroup,
  createDeviceGroup,
  updateDeviceGroup,
  deleteDeviceGroup,
}: DeviceGroupTableProps) => {
  const { t } = useTranslation();
  const { modal } = useModal();
  const [deviceGroupsPageSize, setDeviceGroupsPageSize] = useState<number>(15);

  useEffect(() => {
    if (selectedDeviceGroup) {
      const updatedSelectedDeviceGroup = deviceGroups.find(
        (deviceGroup: DeviceGroupDto) =>
          deviceGroup.deviceGroupId === selectedDeviceGroup.deviceGroupId
      );

      onSelectedDeviceGroupChange(updatedSelectedDeviceGroup || null);
    }
  }, [deviceGroups]);

  const handleAddDeviceGroupClick = (): void => {
    modal({
      title: t('device_groups_add_device_group'),
      content: (
        <div className="md:w-[400px] overflow-y-auto" style={{ maxHeight: '80vh' }}>
          <AddGroupModal
            companyId={companyId}
            handleOnCreateDeviceGroup={createDeviceGroup}
            deviceGroups={deviceGroups}
            autoDeviceGroupTypes={autoDeviceGroupTypes}
          />
        </div>
      ),
    });
  };

  const updateDeviceGroupModal = (deviceGroup: DeviceGroupDto): void => {
    modal({
      title: t('device_groups_edit_device_group'),
      content: (
        <div className="md:w-[400px] overflow-y-auto" style={{ maxHeight: '80vh' }}>
          <EditGroupModal
            deviceGroupToModify={deviceGroup}
            handleOnUpdateDeviceGroup={(body: MutateDeviceGroup): void =>
              updateDeviceGroup(deviceGroup.deviceGroupId, body)
            }
            deviceGroups={deviceGroups}
          />
        </div>
      ),
    });
  };

  const deleteDeviceGroupModal = (deviceGroup: DeviceGroupDto): void => {
    modal({
      title: `${t('device_groups_delete_device_group')}: ${deviceGroup.deviceGroupName}`,
      content: (
        <div className="md:w-[400px] overflow-y-auto" style={{ maxHeight: '80vh' }}>
          <DeleteGroupModal
            handleOnDeleteDeviceGroup={(): void => {
              deleteDeviceGroup(deviceGroup.deviceGroupId, companyId);
            }}
          />
        </div>
      ),
    });
  };

  const rows = useMemo(
    (): DeviceGroupTableRow[] =>
      deviceGroups.map((deviceGroup: DeviceGroupDto) => ({
        id: deviceGroup.deviceGroupId,
        deviceGroupId: deviceGroup.deviceGroupId,
        deviceGroupName: deviceGroup.deviceGroupName,
        parentDeviceGroupName: deviceGroup.parentDeviceGroupName || 'None',
        deviceSensorsCount: deviceGroup.deviceSensors?.length || 0,
      })),
    [deviceGroups]
  );

  const columns = useMemo(
    (): GridColDef[] => [
      {
        field: 'deviceGroupName',
        headerName: t('device_groups_table_sensor_group_name_header'),
        flex: 1.4,
        minWidth: 190,
      },
      {
        field: 'parentDeviceGroupName',
        headerName: t('device_groups_table_parent_group_name_header'),
        flex: 1.2,
        minWidth: 190,
      },
      {
        field: 'deviceSensorsCount',
        headerName: t('device_groups_table_device_count_header'),
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        flex: 0.7,
        minWidth: 120,
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        align: 'right',
        width: 120,
        renderHeader: (): ReactNode => (
          <Tooltip title={<span>{t('device_groups_btn_add')}</span>} arrow placement="top">
            <IconButton aria-label="Add" size="large" onClick={handleAddDeviceGroupClick}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        ),
        renderCell: (params: GridRenderCellParams<DeviceGroupTableRow>): ReactElement => {
          const deviceGroup = deviceGroups.find(
            (group: DeviceGroupDto) => group.deviceGroupId === params.row.deviceGroupId
          );

          if (!deviceGroup) {
            return <></>;
          }

          return (
            <Box className="flex justify-end gap-2 w-full">
              <Tooltip title={<span>{t('device_groups_btn_edit')}</span>} arrow placement="top">
                <IconButton
                  aria-label="Update"
                  size="large"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                    event.stopPropagation();
                    updateDeviceGroupModal(deviceGroup);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={<span>{t('device_groups_btn_delete')}</span>} arrow placement="top">
                <IconButton
                  aria-label="Delete"
                  size="large"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>): void => {
                    event.stopPropagation();
                    deleteDeviceGroupModal(deviceGroup);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [deleteDeviceGroupModal, deviceGroups, handleAddDeviceGroupClick, t, updateDeviceGroupModal]
  );

  return (
    <div className="mr-5" style={{ width: '100%' }}>
      <DataGrid
        autoHeight
        density="compact"
        disableColumnMenu
        disableSelectionOnClick={false}
        hideFooterSelectedRowCount
        pageSize={deviceGroupsPageSize}
        rowsPerPageOptions={[10, 15, 25, 50, 100]}
        onPageSizeChange={(pageSize: number): void => setDeviceGroupsPageSize(pageSize)}
        rows={rows}
        columns={columns}
        selectionModel={selectedDeviceGroup ? [selectedDeviceGroup.deviceGroupId] : []}
        onRowClick={(params: GridRowParams<DeviceGroupTableRow>): void => {
          const deviceGroup = deviceGroups.find(
            (group: DeviceGroupDto) => group.deviceGroupId === params.row.deviceGroupId
          );

          onSelectedDeviceGroupChange(deviceGroup || null);
        }}
      />
    </div>
  );
};

interface DeviceGroupTableProps {
  companyId: number;
  deviceGroups: DeviceGroupDto[];
  autoDeviceGroupTypes: string[];
  selectedDeviceGroup: DeviceGroupDto | null;
  onSelectedDeviceGroupChange: (deviceGroup: DeviceGroupDto | null) => void;
  createDeviceGroup: (deviceGroup: MutateDeviceGroup) => void;
  updateDeviceGroup: (deviceGroupId: number, body: MutateDeviceGroup) => void;
  deleteDeviceGroup: (deviceGroupId: number, companyId?: number) => void;
}

export default DeviceGroupTable;
